import { useState, useCallback } from 'react';
import {
  Ub04Form as Ub04FormType,
  createEmptyUB04Form,
} from '../../../services/ub04Service';
import { Severity } from '../../../types';

interface UseUB04FormManagementProps {
  showSnackbar: (message: string, severity: Severity) => void;
  selectedClaimId: string | null;
}

/**
 * Hook for managing UB04 form state and operations
 */
export const useUB04FormManagement = ({
  showSnackbar,
  selectedClaimId,
}: UseUB04FormManagementProps) => {
  // UB04 form state
  const [ub04Forms, setUb04Forms] = useState<Ub04FormType[]>([]);
  const [currentUb04FormIndex, setCurrentUb04FormIndex] = useState<number>(0);

  /**
   * Get the current form
   */
  const getCurrentForm = useCallback(() => {
    return ub04Forms.length > 0 ? ub04Forms[currentUb04FormIndex] : null;
  }, [ub04Forms, currentUb04FormIndex]);

  /**
   * Get all forms
   */
  const getCurrentForms = useCallback(() => {
    return ub04Forms;
  }, [ub04Forms]);

  /**
   * Get the current form index
   */
  const getCurrentFormIndex = useCallback(() => {
    return currentUb04FormIndex;
  }, [currentUb04FormIndex]);

  /**
   * Change the current form index
   */
  const changeFormIndex = useCallback((index: number) => {
    setCurrentUb04FormIndex(index);
  }, []);

  /**
   * Handle UB04 form changes
   */
  const handleFormChange = useCallback(
    (updatedForm: Ub04FormType) => {
      const updatedForms = [...ub04Forms];
      updatedForms[currentUb04FormIndex] = updatedForm;
      setUb04Forms(updatedForms);
      return true; // Indicates changes were made
    },
    [ub04Forms, currentUb04FormIndex]
  );

  /**
   * Add a new UB04 form
   */
  const addNewForm = useCallback(
    (policyNumber = '') => {
      const newForm = createEmptyUB04Form(policyNumber);
      const updatedForms = [...ub04Forms, newForm];
      setUb04Forms(updatedForms);
      setCurrentUb04FormIndex(updatedForms.length - 1);
      showSnackbar('New UB04 form added', Severity.success);
    },
    [ub04Forms, showSnackbar]
  );

  /**
   * Handle selecting a UB04 claim from search
   */
  const selectClaim = useCallback((selectedForm: Ub04FormType) => {
    setUb04Forms([selectedForm]);
    setCurrentUb04FormIndex(0);
  }, []);

  /**
   * Save the current form
   */
  const saveCurrentForm = useCallback(async () => {
    try {
      if (ub04Forms.length > 0) {
        // Save UB04 form logic would go here
        // For now, just show success message
        showSnackbar('UB04 form saved successfully', Severity.success);
      }
    } catch (error) {
      console.error('Error saving UB04 form:', error);
      showSnackbar('Error saving UB04 form. Please try again.', Severity.error);
      throw error;
    }
  }, [ub04Forms, showSnackbar]);

  /**
   * Handle proceeding with a pending action
   */
  const handleProceedWithAction = useCallback(
    (action: { type: string; payload?: any }) => {
      switch (action.type) {
        case 'changeFormTab':
          setCurrentUb04FormIndex(action.payload.index);
          break;
        case 'addForm':
          addNewForm();
          break;
        case 'selectClaim':
          setUb04Forms(action.payload);
          setCurrentUb04FormIndex(0);
          break;
      }
    },
    [addNewForm]
  );

  return {
    ub04Forms,
    currentUb04FormIndex,
    getCurrentForm,
    getCurrentForms,
    getCurrentFormIndex,
    changeFormIndex,
    handleFormChange,
    addNewForm,
    selectClaim,
    saveCurrentForm,
    handleProceedWithAction,
  };
};

export default useUB04FormManagement;
